import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { SkeletonAdminList } from '../../components/Skeleton';
import { useConfirm } from '../../components/ConfirmModal';

function CreateClinicModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [sftpFolder, setSftpFolder] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.admin.createClinic({ name, sftp_folder_name: sftpFolder || null });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">Create Clinic</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-1">SFTP Folder</label>
            <input
              type="text"
              value={sftpFolder}
              onChange={(e) => setSftpFolder(e.target.value)}
              placeholder="Optional"
              className="w-full border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
            />
          </div>
          {error && (
            <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SyncAllResultsModal({ data, onClose }) {
  const successCount = data.results.filter((r) => r.success).length;
  const failCount = data.results.length - successCount;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-2">Sync All Clinics — Results</h3>
        <p className="text-sm text-gray-600 mb-4">
          {successCount} succeeded, {failCount} failed of {data.totalClinics} total.
        </p>
        <div className="overflow-y-auto border border-[#E6E6E6] rounded-lg divide-y divide-[#E6E6E6]">
          {data.results.map((r) => (
            <div key={r.clinicId} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-text">{r.name}</p>
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  r.success
                    ? 'bg-green-50 text-green-700'
                    : r.skipped
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-destructive-10 text-destructive'
                }`}>
                  {r.success ? 'OK' : r.skipped ? 'Skipped' : 'Failed'}
                </span>
              </div>
              {r.success && r.fileName && (
                <p className="text-xs text-gray-500 mt-1">
                  {r.fileName} — processed {r.processed}, skipped {r.skipped}
                  {r.deactivated ? `, deactivated ${r.deactivated}` : ''}
                </p>
              )}
              {r.skipped && r.reason && (
                <p className="text-xs text-gray-500 mt-1">{r.reason}</p>
              )}
              {!r.success && !r.skipped && (
                <p className="text-xs text-destructive mt-1">{r.error || r.detail}</p>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const SHORT_MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function syncStatusMeta(row) {
  if (!row.sftp_configured) return { label: 'Not configured', cls: 'bg-gray-100 text-gray-600' };
  const s = row.last_sync;
  if (!s) return { label: 'Never synced', cls: 'bg-destructive-10 text-destructive' };
  if (s.status === 'failed') return { label: 'Failed', cls: 'bg-destructive-10 text-destructive' };
  if (s.status === 'empty') return { label: 'Empty file', cls: 'bg-amber-50 text-amber-700' };
  if (row.months_behind >= 1) return { label: `${row.months_behind} mo behind`, cls: 'bg-amber-50 text-amber-700' };
  return { label: 'Up to date', cls: 'bg-green-50 text-green-700' };
}

function syncIssueDetail(row) {
  if (!row.sftp_configured) return 'No SFTP folder configured';
  const s = row.last_sync;
  if (!s) return 'No sync has ever run';
  if (s.status === 'failed') return s.error || 'Sync failed';
  if (s.status === 'empty') return 'Downloaded file had no data rows';
  if (row.months_behind >= 1) return `Latest data is ${row.months_behind} month${row.months_behind > 1 ? 's' : ''} behind`;
  return '';
}

function SyncStatusPanel({ rows, loading }) {
  if (loading) return null;
  const sorted = [...rows].sort((a, b) => Number(b.has_issue) - Number(a.has_issue));
  const issueCount = rows.filter((r) => r.has_issue).length;

  return (
    <div className="bg-white rounded-xl border border-[#E6E6E6] mb-6">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E6E6]">
        <h2 className="text-lg font-bold text-text">Data Sync Status</h2>
        <span className={`text-xs font-medium px-2 py-1 rounded ${issueCount ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
          {issueCount ? `${issueCount} need${issueCount > 1 ? '' : 's'} attention` : 'All clinics current'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-[#E6E6E6] bg-gray-50">
              <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Clinic</th>
              <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-4 py-3">Status</th>
              <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-4 py-3">Data through</th>
              <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-4 py-3">Last sync</th>
              <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const meta = syncStatusMeta(row);
              const detail = syncIssueDetail(row);
              return (
                <tr key={row.clinic_id} className={`border-b border-[#F0F0F0] last:border-0 ${row.has_issue ? 'bg-amber-50/40' : ''}`}>
                  <td className="px-5 py-3 font-medium text-text">{row.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {row.latest_data ? `${SHORT_MONTHS[row.latest_data.month]} ${row.latest_data.year}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {row.last_sync ? new Date(row.last_sync.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-500">{detail || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminClinics() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [syncStatus, setSyncStatus] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const navigate = useNavigate();
  const { confirm, confirmModal } = useConfirm();

  const handleSyncAll = async () => {
    const ok = await confirm({
      title: 'Sync all clinics?',
      message: 'Pulls the latest HIPAA Vault file for every clinic with an SFTP folder configured.',
      confirmLabel: 'Sync all',
    });
    if (!ok) return;
    setSyncing(true);
    setSyncError('');
    try {
      const data = await api.hipaaVault.syncAll();
      setSyncResults(data);
      loadSyncStatus();
    } catch (err) {
      setSyncError(err.message);
    }
    setSyncing(false);
  };

  const loadClinics = () => {
    api.admin.clinics()
      .then(setClinics)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const loadSyncStatus = () => {
    setLoadingStatus(true);
    api.admin.syncStatus()
      .then(setSyncStatus)
      .catch(console.error)
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => {
    loadClinics();
    loadSyncStatus();
  }, []);

  const handleDelete = async (clinic) => {
    const ok = await confirm({
      title: `Delete ${clinic.name}?`,
      message: 'This permanently removes the clinic and unlinks its members and clinicians. This cannot be undone.',
      confirmLabel: 'Delete clinic',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.admin.deleteClinic(clinic.id);
      loadClinics();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-1">Clinics</h1>
          <p className="text-sm text-gray-500">This is a list of all clinics in the database</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD] transition-colors disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync All Clinics'}
          </button>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors flex items-center gap-1"
          >
            <span className="text-lg leading-none">+</span> Create Clinic
          </button>
        </div>
      </div>

      {syncError && (
        <div className="mb-4 p-3 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
          Sync failed: {syncError}
        </div>
      )}

      {!loadingStatus && syncStatus.length > 0 && (
        <SyncStatusPanel rows={syncStatus} loading={loadingStatus} />
      )}

      {loading ? (
        <SkeletonAdminList rows={5} showHeader={false} />
      ) : (
        <div className="bg-white rounded-xl border border-[#E6E6E6]">
          {/* Header row - hidden on mobile */}
          <div className="hidden md:flex items-center px-5 py-3 border-b border-[#E6E6E6] bg-gray-50 rounded-t-xl">
            <div className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</div>
            <div className="w-[250px] text-xs font-semibold text-gray-500 uppercase tracking-wide">SFTP Folder</div>
            <div className="w-[50px]" />
          </div>

          {clinics.map((clinic, idx) => (
            <div
              key={clinic.id}
              className={`flex items-center px-4 md:px-5 py-3.5 ${
                idx !== clinics.length - 1 ? 'border-b border-[#E6E6E6]' : ''
              }`}
            >
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/admin/clinics/${clinic.id}`)}>
                <p className="text-sm font-medium text-text truncate hover:text-primary">{clinic.name}</p>
                {/* SFTP folder shown inline on mobile */}
                {clinic.sftp_folder_name && (
                  <p className="text-xs text-gray-400 md:hidden">{clinic.sftp_folder_name}</p>
                )}
              </div>
              <div className="w-[250px] min-w-0 hidden md:block">
                <p className="text-sm text-gray-500 truncate">{clinic.sftp_folder_name || '\u2014'}</p>
              </div>
              <div className="w-[50px] flex justify-end flex-shrink-0">
                <button
                  onClick={() => handleDelete(clinic)}
                  className="text-gray-400 hover:text-destructive transition-colors p-1"
                  title="Delete clinic"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
          {clinics.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-500">No clinics found</div>
          )}
        </div>
      )}

      {createModalOpen && (
        <CreateClinicModal
          onClose={() => setCreateModalOpen(false)}
          onCreated={loadClinics}
        />
      )}

      {syncResults && (
        <SyncAllResultsModal data={syncResults} onClose={() => setSyncResults(null)} />
      )}
      {confirmModal}
    </div>
  );
}
